import {
  type DataSourceDefinition,
  type FiltersDefinition,
  type SortingsDefinition,
} from '@factorialco/f0-react'
import i18n from 'modules/core/lib/i18n'
import { type HeadcountRow, type ChildHeadcountRow } from 'modules/planning/types/headcount'
import { PARENT_ACTUAL_HEADCOUNT } from 'modules/planning/consts/headcount'
import { PLANNING_PERIOD_STATUS } from 'modules/planning/consts/period_status'
import { type EmployeeOption } from 'modules/planning/hooks/headcount/useHeadcountEmployeeSource'
import { type JobCatalogOption } from 'modules/planning/hooks/headcount/useHeadcountJobCatalogSource'
import { type LocationOption } from 'modules/planning/hooks/headcount/useHeadcountLocationSource'
import { getCurrencySymbolFromCode } from 'modules/core/lib/formatCurrencyUtils'
import type { PlanningCapability } from 'modules/planning/roles/capabilities'
import type { PlanningValueObjectsRequestStatusEnum } from 'generated/resources/graphql'

export type ColumnDefinitionSources = {
  jobCatalogSource?: DataSourceDefinition<JobCatalogOption, FiltersDefinition, SortingsDefinition>
  reporteesEmployeeSource?: DataSourceDefinition<
    EmployeeOption,
    FiltersDefinition,
    SortingsDefinition
  >
  locationSource?: DataSourceDefinition<LocationOption, FiltersDefinition, SortingsDefinition>
  currency?: string
  periodStatus?: string
  capabilities?: Set<PlanningCapability>
  currentEmployeeId?: string
}

export type HeadcountColumn = ReturnType<typeof getColumnDefinition>[number]

type EditType = 'text' | 'number' | 'date' | 'select' | 'display-only' | 'disabled' | 'money'

const formatSalaryWithCurrency = (salary: string, currency: string): string => {
  if (!salary) return ''

  const amount = Number(salary)
  if (isNaN(amount)) return salary

  if (!currency) return salary

  try {
    return new Intl.NumberFormat(i18n.locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return salary
  }
}

const editTypeForRow =
  (requestEditType: EditType, canEdit: boolean, canEditOwn: boolean, currentEmployeeId?: string) =>
  (item: HeadcountRow) => {
    if (item.rowKind === 'parent') return 'display-only' as const
    if (item.rowKind === 'headcount_request') {
      if (item.status === 'approved' || item.status === 'rejected') return 'disabled' as const
      if (canEdit) return requestEditType
      if (canEditOwn && currentEmployeeId && item.employeeId === currentEmployeeId)
        return requestEditType
      return 'display-only' as const
    }
    return 'disabled' as const
  }

const getDotTagColor = (item: ChildHeadcountRow) => {
  if (item.parentType === PARENT_ACTUAL_HEADCOUNT) return 'viridian' as const
  if (item.type === 'new') return 'yellow' as const
  if (item.type === 'backfill') return 'indigo' as const
  return 'smoke' as const
}

const getDotTagLabel = (item: ChildHeadcountRow) => {
  if (item.parentType === PARENT_ACTUAL_HEADCOUNT)
    return i18n.t('planning.employee_sheet.request_types.current')
  if (item.type === 'new') return i18n.t('planning.employee_sheet.request_types.new')
  if (item.type === 'backfill') return i18n.t('planning.employee_sheet.request_types.backfill')
  if (item.type === 'replacement')
    return i18n.t('planning.employee_sheet.request_types.replacement')
  return item.type
}

const APPROVAL_STATUS_DOT_TAG_COLORS: Record<
  PlanningValueObjectsRequestStatusEnum,
  'viridian' | 'yellow' | 'smoke' | 'indigo' | 'barbie' | string
> = {
  approved: 'positive',
  pending: 'warning',
  rejected: 'critical',
  draft: 'neutral',
}

const getApprovalStatusLabel = (status: PlanningValueObjectsRequestStatusEnum): string => {
  return {
    approved: i18n.t('planning.employee_sheet.approval_status.approved'),
    pending: i18n.t('planning.employee_sheet.approval_status.pending'),
    rejected: i18n.t('planning.employee_sheet.approval_status.rejected'),
    draft: i18n.t('planning.employee_sheet.approval_status.draft'),
  }[status]
}

const shouldShowRequestStatus = (
  periodStatus: string | undefined,
  requestStatus: PlanningValueObjectsRequestStatusEnum | null,
  capabilities?: Set<PlanningCapability>
): boolean => {
  if (!requestStatus) return false
  return (
    capabilities?.has('approveRejectRequest') ||
    capabilities?.has('editPeriod') ||
    periodStatus === PLANNING_PERIOD_STATUS.TO_BE_REVIEWED ||
    periodStatus === PLANNING_PERIOD_STATUS.CLOSED ||
    requestStatus === 'approved' ||
    requestStatus === 'rejected'
  )
}

export function getColumnDefinition({
  jobCatalogSource,
  reporteesEmployeeSource,
  locationSource,
  currency,
  periodStatus,
  capabilities,
  currentEmployeeId,
}: ColumnDefinitionSources = {}) {
  const canEdit = capabilities?.has('editRequest') ?? false
  const canEditOwn = capabilities?.has('createRequestIfPeriodActive') ?? false
  return [
    {
      id: 'type' as const,
      width: 200,
      label: i18n.t('planning.employee_sheet.columns.type'),
      render: (item: HeadcountRow) => {
        if (item.rowKind === 'parent') {
          return {
            type: 'text' as const,
            value: item.label,
          }
        }

        return {
          type: 'dotTag' as const,
          value: {
            label: getDotTagLabel(item),
            color: getDotTagColor(item),
          },
        }
      },
      editType: editTypeForRow('select', canEdit, canEditOwn, currentEmployeeId),
      selectConfig: {
        options: [
          { value: 'new', label: i18n.t('planning.employee_sheet.request_types.new') },
          { value: 'backfill', label: i18n.t('planning.employee_sheet.request_types.backfill') },
        ],
      },
    },
    {
      id: 'name' as const,
      label: i18n.t('planning.employee_sheet.columns.name'),
      align: 'left' as const,
      render: (item: HeadcountRow) => {
        if (item.rowKind === 'parent') {
          return {
            type: 'count' as const,
            value: { label: item.count },
          }
        }

        if (item.rowKind === 'employee') {
          return {
            type: 'person' as const,
            value: {
              firstName: item.employeeName,
              lastName: '',
              src: item.avatarUrl,
            },
          }
        }
        return {
          type: 'text' as const,
          value: item.requestCode,
        }
      },
      editType: editTypeForRow('disabled', canEdit, canEditOwn, currentEmployeeId),
    },
    {
      id: 'status' as const,
      width: 160,
      label: i18n.t('planning.employee_sheet.columns.status'),
      render: (item: HeadcountRow) => {
        if (item.rowKind !== 'headcount_request') {
          return { type: 'text' as const, value: null }
        }

        if (!shouldShowRequestStatus(periodStatus, item.status, capabilities)) {
          return { type: 'text' as const, value: null }
        }

        if (!item.status) {
          return null
        }

        return {
          type: 'status' as const,
          value: {
            label: getApprovalStatusLabel(item.status),
            status: APPROVAL_STATUS_DOT_TAG_COLORS[item.status],
          },
        }
      },
      editType: editTypeForRow('disabled', canEdit, canEditOwn, currentEmployeeId),
    },
    {
      id: 'managerId' as const,
      label: i18n.t('planning.employee_sheet.columns.direct_manager'),
      render: (item: HeadcountRow) => {
        if (item.rowKind === 'parent') {
          return { type: 'text' as const, value: null }
        }

        if (item.managerName) {
          return {
            type: 'person' as const,
            value: {
              firstName: item.managerName,
              lastName: '',
              src: item.managerAvatarUrl,
            },
          }
        }

        return {
          type: 'text' as const,
          value: null,
        }
      },
      editType: editTypeForRow('select', canEdit, canEditOwn, currentEmployeeId),
      ...(reporteesEmployeeSource
        ? {
            selectConfig: {
              showSearchBox: true,
              source: reporteesEmployeeSource,
              mapOptions: (employee: EmployeeOption) => ({
                value: employee.id,
                label: employee.fullName,
              }),
              defaultItem: (item: HeadcountRow) =>
                item.rowKind !== 'parent' && item.managerId
                  ? { value: item.managerId, label: item.managerName }
                  : undefined,
            },
          }
        : {}),
    },
    {
      id: 'jobCatalogTreeNodeUuid' as const,
      label: i18n.t('planning.employee_sheet.columns.role'),
      render: (item: HeadcountRow) => {
        if (item.rowKind === 'parent') return { type: 'text' as const, value: null }
        return { type: 'text' as const, value: item.jobCatalogLabel ?? null }
      },
      editType: editTypeForRow('select', canEdit, canEditOwn, currentEmployeeId),
      ...(jobCatalogSource
        ? {
            selectConfig: {
              showSearchBox: true,
              source: jobCatalogSource,
              mapOptions: (option: JobCatalogOption) => ({
                value: option.id,
                label: option.name,
              }),
              defaultItem: (item: HeadcountRow) =>
                item.rowKind !== 'parent' && item.jobCatalogTreeNodeUuid
                  ? { value: item.jobCatalogTreeNodeUuid, label: item.jobCatalogLabel }
                  : undefined,
            },
          }
        : {}),
    },
    {
      id: 'salary' as const,
      label: i18n.t('planning.employee_sheet.columns.salary'),
      align: 'right' as const,
      render: (item: HeadcountRow) => {
        const amount =
          item.rowKind === 'headcount_request' ? Number(item.salary) : Number(item.salary) / 100
        const salary = formatSalaryWithCurrency(String(amount), item.currency)

        if (item.rowKind === 'parent') {
          return {
            type: 'summary' as const,
            value: {
              label: salary,
            },
          }
        }

        return {
          type: 'text' as const,
          value: salary,
        }
      },
      editType: editTypeForRow('money', canEdit, canEditOwn, currentEmployeeId),
      numberConfig: {
        min: 0,
        units: currency ? getCurrencySymbolFromCode(currency) : '€',
        unitsPosition: 'after',
      },
    },
    {
      id: 'workingTime' as const,
      label: i18n.t('planning.employee_sheet.columns.working_time'),
      align: 'right' as const,
      render: (item: HeadcountRow) => ({
        type: 'text' as const,
        value:
          item.rowKind === 'parent'
            ? null
            : item.workingTime
              ? `${Number(item.workingTime).toFixed(2)}%`
              : null,
      }),
      editType: editTypeForRow('number', canEdit, canEditOwn, currentEmployeeId),
      numberConfig: {
        min: 0,
        max: 100,
        decimals: 2,
        units: '%',
        unitsPosition: 'after',
      },
    },
    {
      id: 'locationId' as const,
      label: i18n.t('planning.employee_sheet.columns.workplace'),
      width: 200,
      render: (item: HeadcountRow) => ({
        type: 'text' as const,
        value: item.rowKind === 'parent' ? null : item.workplace,
      }),
      editType: editTypeForRow('select', canEdit, canEditOwn, currentEmployeeId),
      ...(locationSource
        ? {
            selectConfig: {
              showSearchBox: true,
              source: locationSource,
              mapOptions: (location: LocationOption) => ({
                value: location.id,
                label: location.name,
              }),
              defaultItem: (item: HeadcountRow) =>
                item.rowKind !== 'parent' && item.locationId
                  ? { value: item.locationId, label: item.workplace }
                  : undefined,
            },
          }
        : {}),
    },
    {
      id: 'startDate' as const,
      width: 200,
      label: i18n.t('planning.employee_sheet.columns.start_date'),
      render: (item: HeadcountRow) => ({
        type: 'date' as const,
        value: item.rowKind === 'parent' ? null : (item.startDate?.split('T')[0] ?? null),
      }),
      editType: editTypeForRow('date', canEdit, canEditOwn, currentEmployeeId),
    },
    {
      id: 'endDate' as const,
      width: 200,
      label: i18n.t('planning.employee_sheet.columns.end_date'),
      render: (item: HeadcountRow) => ({
        type: 'date' as const,
        value: item.rowKind === 'parent' ? null : (item.endDate?.split('T')[0] ?? null),
      }),
      editType: editTypeForRow('date', canEdit, canEditOwn, currentEmployeeId),
    },
  ]
}
